import { Alert, LoadingPanel, StatusPanel } from '@dispatch/ui';
import { useCallback, useEffect, useMemo, useState, type SyntheticEvent } from 'react';
import { apiRequest, errorMessage } from './api.js';
import { FormBuilderPanel } from './FormBuilderPanel.js';
import type { TemplateFolder, TemplateSummary } from './types.js';

const ALL_FORMS_SELECTION = 'all';
const UNFILED_FORMS_SELECTION = 'unfiled';

function FolderIcon() {
  return (
    <svg className="form-folder-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M3.75 4.5h5.1c.5 0 .98.2 1.33.55l1.2 1.2h8.87A1.75 1.75 0 0 1 22 8v9.75a1.75 1.75 0 0 1-1.75 1.75H3.75A1.75 1.75 0 0 1 2 17.75V6.25A1.75 1.75 0 0 1 3.75 4.5Z"
      />
    </svg>
  );
}

export function TemplatesPanel({
  businessId,
  csrfToken,
  supportAccessId,
  canBuild,
  canManageFolders,
}: {
  businessId: string;
  csrfToken: string;
  supportAccessId?: string | undefined;
  canBuild: boolean;
  canManageFolders: boolean;
}) {
  const [folders, setFolders] = useState<TemplateFolder[]>([]);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [folderName, setFolderName] = useState('');
  const [formName, setFormName] = useState('New job sheet form');
  const [selectedFolderId, setSelectedFolderId] = useState<string>(ALL_FORMS_SELECTION);
  const [busy, setBusy] = useState(false);
  const [builderTemplate, setBuilderTemplate] = useState<TemplateSummary>();
  const [builderVersionId, setBuilderVersionId] = useState<string>();

  const activeFolders = useMemo(
    () => folders.filter((folder) => folder.status === 'active'),
    [folders],
  );

  const folderNameById = useMemo(() => {
    const names = new Map<string, string>();
    for (const folder of folders) names.set(folder.id, folder.name);
    return names;
  }, [folders]);

  const filteredForms = useMemo(() => {
    if (selectedFolderId === ALL_FORMS_SELECTION) return templates;
    if (selectedFolderId === UNFILED_FORMS_SELECTION) {
      return templates.filter((template) => template.folder_id === null);
    }

    return templates.filter((template) => template.folder_id === selectedFolderId);
  }, [selectedFolderId, templates]);

  const selectedFolderName = useMemo(() => {
    if (selectedFolderId === ALL_FORMS_SELECTION) return 'All forms';
    if (selectedFolderId === UNFILED_FORMS_SELECTION) return 'No folder';
    return folderNameById.get(selectedFolderId) ?? 'Selected folder';
  }, [folderNameById, selectedFolderId]);

  const loadForms = useCallback(
    async (preferredSelection?: string) => {
      setLoading(true);
      setError(undefined);

      try {
        const [folderResult, templateResult] = await Promise.all([
          apiRequest<{ folders: TemplateFolder[] }>(
            `/api/v1/businesses/${businessId}/template-folders`,
            { supportAccessId },
          ),
          apiRequest<{ templates: TemplateSummary[] }>(
            `/api/v1/businesses/${businessId}/templates`,
            { supportAccessId },
          ),
        ]);

        setFolders(folderResult.folders);
        setTemplates(templateResult.templates);

        if (preferredSelection) {
          setSelectedFolderId(preferredSelection);
          return;
        }

        setSelectedFolderId((current) => {
          if (
            current === ALL_FORMS_SELECTION ||
            current === UNFILED_FORMS_SELECTION ||
            folderResult.folders.some((folder) => folder.id === current)
          ) {
            return current;
          }

          return ALL_FORMS_SELECTION;
        });
      } catch (caught) {
        setError(errorMessage(caught));
      } finally {
        setLoading(false);
      }
    },
    [businessId, supportAccessId],
  );

  useEffect(() => {
    const run = async () => {
      await Promise.resolve();
      await loadForms();
    };

    void run();
  }, [loadForms]);

  async function createFolder(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    setNotice(undefined);

    try {
      const result = await apiRequest<{ folder: TemplateFolder }>(
        `/api/v1/businesses/${businessId}/template-folders`,
        {
          method: 'POST',
          csrfToken,
          supportAccessId,
          body: { name: folderName },
        },
      );

      setNotice(`Folder created: ${result.folder.name}`);
      setFolderName('');
      await loadForms(result.folder.id);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function createForm(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    setNotice(undefined);

    const targetFolderId =
      selectedFolderId === ALL_FORMS_SELECTION || selectedFolderId === UNFILED_FORMS_SELECTION
        ? undefined
        : selectedFolderId;

    try {
      const result = await apiRequest<{ template: TemplateSummary }>(
        `/api/v1/businesses/${businessId}/templates`,
        {
          method: 'POST',
          csrfToken,
          supportAccessId,
          body: {
            name: formName,
            folderId: targetFolderId,
            availability: [{ scopeType: 'business' }],
          },
        },
      );

      setNotice(`Form draft created: ${result.template.name}`);
      setFormName('');
      if (result.template.current_draft_version_id) {
        setBuilderTemplate(result.template);
        setBuilderVersionId(result.template.current_draft_version_id);
      }
      await loadForms(targetFolderId ?? UNFILED_FORMS_SELECTION);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function openBuilder(template: TemplateSummary) {
    setError(undefined);
    setNotice(undefined);
    if (template.current_draft_version_id) {
      setBuilderTemplate(template);
      setBuilderVersionId(template.current_draft_version_id);
      return;
    }

    setBusy(true);
    try {
      const result = await apiRequest<{ draftVersion: { id: string } }>(
        `/api/v1/businesses/${businessId}/templates/${template.id}/drafts`,
        {
          method: 'POST',
          csrfToken,
          supportAccessId,
        },
      );
      const updatedTemplate = { ...template, current_draft_version_id: result.draftVersion.id };
      setBuilderTemplate(updatedTemplate);
      setBuilderVersionId(result.draftVersion.id);
      await loadForms(selectedFolderId);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  if (builderTemplate && builderVersionId) {
    return (
      <FormBuilderPanel
        businessId={businessId}
        csrfToken={csrfToken}
        supportAccessId={supportAccessId}
        template={builderTemplate}
        versionId={builderVersionId}
        onClose={() => {
          setBuilderTemplate(undefined);
          setBuilderVersionId(undefined);
          void loadForms(selectedFolderId);
        }}
      />
    );
  }

  if (loading) return <LoadingPanel message="Loading form records…" />;

  return (
    <section className="panel" aria-labelledby="forms-heading">
      <h2 id="forms-heading">Forms</h2>
      <p>
        Forms provides folders, draft and published versions, managed reference data and the Phase 4
        Form Builder. Open a draft to design pages, sections, fields, logic, calculations and
        runtime preview. PDF layout and jobs remain unavailable until their approved phases.
      </p>

      {error ? <Alert title="Form request failed">{error}</Alert> : null}
      {notice ? <Alert tone="success">{notice}</Alert> : null}

      <StatusPanel heading="Form records" tone="neutral">
        <p>
          {folders.length} folder{folders.length === 1 ? '' : 's'} and {templates.length} form
          {templates.length === 1 ? '' : 's'} in this business.
        </p>
      </StatusPanel>

      <div className="forms-explorer-layout">
        <section className="card forms-folders-card" aria-labelledby="form-folders-heading">
          <div>
            <h3 id="form-folders-heading">Form folders</h3>
            <p>Select a folder to view the forms inside it.</p>
          </div>

          <div className="form-folder-grid" aria-label="Form folders">
            <button
              type="button"
              className="secondary"
              aria-pressed={selectedFolderId === ALL_FORMS_SELECTION}
              onClick={() => setSelectedFolderId(ALL_FORMS_SELECTION)}
            >
              <FolderIcon />
              <span>All forms ({templates.length})</span>
            </button>

            <button
              type="button"
              className="secondary"
              aria-pressed={selectedFolderId === UNFILED_FORMS_SELECTION}
              onClick={() => setSelectedFolderId(UNFILED_FORMS_SELECTION)}
            >
              <FolderIcon />
              <span>
                No folder ({templates.filter((template) => template.folder_id === null).length})
              </span>
            </button>

            {activeFolders.map((folder) => {
              const folderFormCount = templates.filter(
                (template) => template.folder_id === folder.id,
              ).length;

              return (
                <button
                  key={folder.id}
                  type="button"
                  className="secondary"
                  aria-pressed={selectedFolderId === folder.id}
                  onClick={() => setSelectedFolderId(folder.id)}
                >
                  <FolderIcon />
                  <span>
                    {folder.name} ({folderFormCount})
                  </span>
                </button>
              );
            })}
          </div>

          {activeFolders.length === 0 ? (
            <Alert tone="info">No folders have been created yet.</Alert>
          ) : null}

          {canManageFolders ? (
            <form
              className="form-folder-create-form"
              onSubmit={(event) => void createFolder(event)}
            >
              <h4>Create folder</h4>
              <div className="form-folder-create-row">
                <div className="form-field">
                  <label htmlFor="form-folder-name">Folder name</label>
                  <input
                    id="form-folder-name"
                    required
                    value={folderName}
                    onChange={(event) => setFolderName(event.target.value)}
                  />
                </div>
                <button
                  className="form-control-submit"
                  type="submit"
                  disabled={busy || folderName.trim().length === 0}
                >
                  Create folder
                </button>
              </div>
            </form>
          ) : null}
        </section>

        {canBuild ? (
          <form
            className="card form-grid form-control-card"
            onSubmit={(event) => void createForm(event)}
          >
            <h3>Create form draft</h3>
            <p>
              New forms are created in the currently selected folder. Select “All forms” or “No
              folder” to create an unfiled form.
            </p>

            <div className="form-field">
              <label htmlFor="form-name">Form name</label>
              <input
                id="form-name"
                required
                value={formName}
                onChange={(event) => setFormName(event.target.value)}
              />
            </div>

            <div className="form-field">
              <label htmlFor="form-folder">Destination folder</label>
              <select
                id="form-folder"
                value={
                  selectedFolderId === ALL_FORMS_SELECTION ||
                  selectedFolderId === UNFILED_FORMS_SELECTION
                    ? ''
                    : selectedFolderId
                }
                onChange={(event) =>
                  setSelectedFolderId(event.target.value || UNFILED_FORMS_SELECTION)
                }
              >
                <option value="">No folder</option>
                {activeFolders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              className="form-control-submit"
              type="submit"
              disabled={busy || formName.trim().length === 0}
            >
              Create draft
            </button>
          </form>
        ) : null}
      </div>

      <section aria-labelledby="form-list-heading">
        <h3 id="form-list-heading">{selectedFolderName}</h3>
        <p>
          Showing {filteredForms.length} form{filteredForms.length === 1 ? '' : 's'}.
        </p>

        {filteredForms.length === 0 ? (
          <Alert tone="info">No forms in this folder yet.</Alert>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Folder</th>
                  <th>Status</th>
                  <th>Draft version</th>
                  <th>Published version</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredForms.map((template) => (
                  <tr key={template.id}>
                    <td>{template.name}</td>
                    <td>
                      {template.folder_id
                        ? (folderNameById.get(template.folder_id) ?? 'Unknown')
                        : 'No folder'}
                    </td>
                    <td>{template.lifecycle_status}</td>
                    <td>{template.current_draft_version_id ?? 'None'}</td>
                    <td>{template.published_version_number ?? 'None'}</td>
                    <td>
                      {canBuild && template.lifecycle_status !== 'archived' ? (
                        <button
                          type="button"
                          className="secondary"
                          disabled={busy}
                          onClick={() => void openBuilder(template)}
                        >
                          {template.current_draft_version_id ? 'Open builder' : 'Create new draft'}
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </section>
  );
}
